import React, { useMemo, useState } from 'react';
import { FileText, Upload, CheckCircle, Clock, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useCatalogData } from '../hooks/useCatalogData';

const Quotes = () => {
  const { products: catalogProducts = [] } = useCatalogData();
  const products = useMemo(() => (Array.isArray(catalogProducts) ? catalogProducts : []), [catalogProducts]);
  const [quotes] = useState([]);
  const [showNewQuoteForm, setShowNewQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    message: '',
    csvFile: null
  });
  const [selectedProducts, setSelectedProducts] = useState([]);

  const handleSubmitQuote = (e) => {
    e.preventDefault();
    toast({
      title: 'Quote Request Submitted',
      description: 'We will review your request and respond within 24 hours.'
    });
    setShowNewQuoteForm(false);
    setFormData({ message: '', csvFile: null });
    setSelectedProducts([]);
  };

  const getProductById = (productId) => products.find((p) => p.id === productId || p.raw?.product_id === productId);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="text-green-600" size={24} />;
      case 'pending':
        return <Clock className="text-yellow-600" size={24} />;
      case 'rejected':
        return <X className="text-red-600" size={24} />;
      default:
        return <FileText className="text-gray-600" size={24} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Quote Management</h1>
          <button
            onClick={() => setShowNewQuoteForm(true)}
            className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition font-semibold"
          >
            Request New Quote
          </button>
        </div>

        {/* New Quote Form Modal */}
        {showNewQuoteForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Request Bulk Quote</h2>
                  <button
                    onClick={() => setShowNewQuoteForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmitQuote} className="p-6">
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload CSV/Excel File (Optional)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-500 transition cursor-pointer">
                    <Upload className="mx-auto text-gray-400 mb-2" size={48} />
                    <p className="text-sm text-gray-600 mb-1">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">CSV or Excel file with product SKUs and quantities</p>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={(e) => {
                        setFormData({ ...formData, csvFile: e.target.files[0] });
                        toast({ title: 'File uploaded successfully' });
                      }}
                    />
                  </div>
                  {formData.csvFile && (
                    <p className="text-sm text-green-600 mt-2">
                      File uploaded: {formData.csvFile.name}
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message / Additional Requirements
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    placeholder="Describe your requirements, delivery timeline, or any special requests..."
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                    required
                  />
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">
                    Our team will review your quote request and respond with pricing within 24 hours.
                  </p>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowNewQuoteForm(false)}
                    className="flex-1 border border-gray-300 py-3 rounded hover:bg-gray-50 transition font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold"
                  >
                    Submit Quote Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Quotes List */}
        {quotes.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Quotes Yet</h2>
            <p className="text-gray-600 mb-6">Request a quote for bulk orders or custom pricing.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {quotes.map((quote) => (
              <div key={quote.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Quote Header */}
                <div className="bg-gray-50 px-6 py-4 border-b">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center space-x-4 mb-4 md:mb-0">
                      {getStatusIcon(quote.status)}
                      <div>
                        <h3 className="font-bold text-gray-900">Quote #{quote.id}</h3>
                        <p className="text-sm text-gray-600">
                          Submitted on {new Date(quote.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(quote.status)}`}>
                      {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Quote Details */}
                <div className="p-6">
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Requested Items</h4>
                    <div className="space-y-3">
                      {quote.items.map((item) => {
                        const product = getProductById(item.productId);
                        if (!product) return null;
                        
                        return (
                          <div key={item.productId} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <img
                                src={product.image || '/placeholder.jpg'}
                                alt={product.name}
                                onError={(e) => {
                                  e.target.src = '/placeholder.jpg';
                                }}
                                className="w-16 h-16 object-cover rounded"
                              />
                              <div>
                                <p className="font-semibold text-gray-900">{product.name}</p>
                                <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">Qty: {item.quantity}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {quote.message && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Message</h4>
                      <p className="text-gray-600 bg-gray-50 p-4 rounded">{quote.message}</p>
                    </div>
                  )}

                  {quote.response && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Response</h4>
                      <p className="text-gray-600 bg-green-50 p-4 rounded border-l-4 border-green-500">
                        {quote.response}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    {quote.status === 'approved' && (
                      <button className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition font-semibold">
                        Accept & Place Order
                      </button>
                    )}
                    <button className="border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 transition font-semibold">
                      Download Quote
                    </button>
                    {quote.status === 'pending' && (
                      <button className="border border-gray-300 px-6 py-2 rounded hover:bg-gray-50 transition font-semibold">
                        Modify Request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Quotes;
